<template>
    <!-- 管理员-项目管理/观思问配置 -->
    <div class="observe-config wrapper" v-loading="loading">
        <proTitle></proTitle>

        <div class="phase-list" v-if="phaseList.length">
            <div class="phase-card" :class="{ 'is-disabled': !phase.gswEnabled }" v-for="phase in phaseList"
                :key="phase.id">
                <div class="phase-card-head flex-align-between">
                    <div class="head-left flex-align">
                        <i class="el-icon-location pin-icon"></i>
                        <span class="phase-title">{{ phase.displayName }}</span>
                        <el-tag size="mini" :type="phase.gswEnabled ? 'success' : 'info'">
                            {{ phase.gswEnabled ? '前端已启用' : '前端未启用' }}
                        </el-tag>
                    </div>
                    <div class="head-actions">
                        <el-button size="mini" plain :loading="phase._toggling" @click="toggleFrontend(phase)">
                            {{ phase.gswEnabled ? '取消启用' : '启用' }}
                        </el-button>
                        <el-button size="mini" plain @click="openQuestionDialog(phase)">编辑问题</el-button>
                        <el-button size="mini" plain @click="openAnswerDialog(phase)">编辑作答日期</el-button>
                    </div>
                </div>

                <!-- 作答与总结日期摘要 -->
                <div class="gsw-schedule">
                    <p class="schedule-title">作答与总结日期</p>
                    <template v-if="phase.periods.length">
                        <p class="schedule-line" v-for="(line, index) in scheduleLines(phase)" :key="index"
                            :class="{ 'is-muted': line.muted, 'is-empty': line.empty }">{{ line.text }}</p>
                    </template>
                    <p class="schedule-line is-empty" v-else>请先在阶段管理中配置周次</p>
                </div>

                <!-- 用户组tab：全部 + 本阶段适用用户组 -->
                <div class="group-tabs">
                    <span class="group-tab" :class="{ 'is-active': phase.activeGroup === 'all' }"
                        @click="phase.activeGroup = 'all'">全部</span>
                    <span class="group-tab" :class="{ 'is-active': phase.activeGroup === group.key }"
                        v-for="group in phase.groupOptions" :key="group.key"
                        @click="phase.activeGroup = group.key">
                        {{ group.label }}
                        <i class="group-tag" :class="isCustomGroup(phase, group.key) ? 'is-custom' : 'is-shared'">
                            {{ isCustomGroup(phase, group.key) ? '单独' : '共用' }}
                        </i>
                    </span>
                </div>

                <!-- 观思问题目预览 -->
                <div class="phase-blocks">
                    <div class="phase-block" v-for="item in questionBlocks(phase)" :key="item.type"
                        :class="item.type">
                        <p class="block-label">
                            <i :class="item.icon"></i>
                            {{ item.label }}
                        </p>
                        <!-- <el-tooltip :content="item.text" placement="top" :disabled="!item.text"> -->
                            <p class="block-text" :title="item.text || ''">{{ item.text || '未配置' }}</p>
                        <!-- </el-tooltip> -->
                    </div>
                </div>
            </div>
        </div>
        <el-empty v-else-if="!loading" description="暂无已启用的阶段，请先在「阶段管理」中启用阶段后，再配置观思问"></el-empty>

        <!-- 编辑问题 -->
        <el-dialog title="观思问配置 · 编辑问题" :visible.sync="questionDialogVisible" width="620px" top="6vh"
            :close-on-click-modal="false">
            <p class="dialog-subtitle" v-if="editingPhase">{{ editingPhase.displayName }}</p>
            <div class="group-tabs modal-group-tabs" v-if="editingPhase">
                <span class="group-tab" :class="{ 'is-active': editingGroup === 'all' }"
                    @click="switchEditingGroup('all')">全部</span>
                <span class="group-tab" :class="{ 'is-active': editingGroup === group.key }"
                    v-for="group in editingPhase.groupOptions" :key="group.key"
                    @click="switchEditingGroup(group.key)">
                    {{ group.label }}
                    <i class="group-tag"
                        :class="isCustomGroup(editingPhase, group.key) ? 'is-custom' : 'is-shared'">
                        {{ isCustomGroup(editingPhase, group.key) ? '单独' : '共用' }}
                    </i>
                </span>
            </div>
            <p class="form-hint">「全部」为共用题库：未单独修改的用户组共用同一套问题。在某一用户组中修改后，仅该组变更，其他组保持原样。勾选下方选项可一次覆盖所有用户组。</p>
            <el-checkbox v-model="syncAllGroups" v-show="editingGroup === 'all'" class="sync-checkbox">
                保存时同步到全部用户组（含已单独修改的）
            </el-checkbox>
            <el-form label-width="90px" size="small" @submit.native.prevent>
                <template v-for="item in questionTypes">
                    <el-form-item :label="item.name" :key="item.type + 'label'">
                        <el-input v-model.trim="questionForm[item.type + 'Label']" placeholder="标签名称"
                            style="width:200px"></el-input>
                    </el-form-item>
                    <el-form-item :label="item.name + '问题'" required :key="item.type + 'text'">
                        <el-input type="textarea" :rows="2" v-model.trim="questionForm[item.type + 'Text']"
                            :placeholder="`请输入${item.name}类详细问题`"></el-input>
                    </el-form-item>
                </template>
            </el-form>
            <span slot="footer">
                <el-button size="small" @click="questionDialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="savingQuestions" @click="saveQuestions">保存</el-button>
            </span>
        </el-dialog>

        <!-- 编辑作答日期 -->
        <el-dialog title="观思问配置 · 编辑作答日期" :visible.sync="answerDialogVisible" width="620px" top="6vh"
            :close-on-click-modal="false">
            <p class="dialog-subtitle" v-if="editingPhase">{{ editingPhase.displayName }}</p>
            <div class="section-title">观思问作答日期</div>
            <el-radio-group v-model="answerForm.mode" size="small">
                <el-radio label="fixedDates">固定日期</el-radio>
                <el-radio label="minDays">固定最小作答天数</el-radio>
            </el-radio-group>
            <div class="week-periods">
                <span class="week-periods-label">阶段周次（来自阶段编辑）</span>
                <span class="week-periods-text">{{ weekPeriodsText }}</span>
            </div>

            <div v-if="answerForm.mode === 'fixedDates'" class="answer-panel">
                <div class="flex-align-between" style="margin-bottom:8px">
                    <span class="form-hint" style="margin:0">在周次范围内选择作答日期</span>
                    <div>
                        <el-button type="text" size="small" @click="generateAnswerDates">根据周次生成</el-button>
                        <span class="form-hint">已选 {{ answerForm.fixedDates.length }} 天</span>
                    </div>
                </div>
                <el-date-picker v-model="answerForm.fixedDates" type="dates" value-format="yyyy-MM-dd"
                    placeholder="选择作答日期（可多选）" :picker-options="answerPickerOptions"
                    style="width:100%"></el-date-picker>
            </div>
            <div v-else class="answer-panel">
                <p class="form-hint">按阶段周次分别设置每周最少作答天数（不超过该周总天数）</p>
                <div class="min-week-row flex-align" v-for="(week, index) in answerForm.weekMinDays" :key="index">
                    <span class="min-week-label">{{ weekLabel(index) }}</span>
                    <span class="min-week-range">{{ editingPeriods[index].start }} ~ {{ editingPeriods[index].end
                        }}（共{{ weekDayCount(editingPeriods[index]) }}天）</span>
                    <span>最少作答</span>
                    <el-input-number v-model="week.value" :min="1" :max="weekDayCount(editingPeriods[index])"
                        size="mini" style="margin:0 8px"></el-input-number>
                    <span>天</span>
                </div>
            </div>

            <div class="section-title">观思问总结</div>
            <el-radio-group v-model="answerForm.summaryMode" size="small">
                <el-radio label="weekly">周总结</el-radio>
                <el-radio label="multiWeek">多周总结</el-radio>
            </el-radio-group>
            <div v-if="answerForm.summaryMode === 'weekly'" class="answer-panel">
                <p class="form-hint">按每一周分别设置总结日期（选填，须在该周范围内）</p>
                <div class="min-week-row flex-align" v-for="(item, index) in answerForm.weekSummaryDates" :key="index">
                    <span class="min-week-label">{{ weekLabel(index) }}</span>
                    <span class="min-week-range">{{ editingPeriods[index].start }} ~ {{ editingPeriods[index].end
                        }}</span>
                    <el-date-picker v-model="item.date" type="date" value-format="yyyy-MM-dd" size="mini"
                        placeholder="总结日期（选填）" :picker-options="weekPickerOptions(editingPeriods[index])"
                        style="width:170px"></el-date-picker>
                </div>
            </div>
            <div v-else class="answer-panel">
                <div class="flex-align-between" style="margin-bottom:8px">
                    <span class="form-hint" style="margin:0">每组多周选择一个总结日期（选填）；总结日期可晚于所选周次结束，须在项目执行期内</span>
                    <el-button type="text" size="small" @click="addMultiSummary">添加多周总结</el-button>
                </div>
                <p class="form-hint" v-if="!answerForm.multiWeekSummaries.length">点击「添加多周总结」创建合并总结</p>
                <div class="min-week-row flex-align" v-for="(item, index) in answerForm.multiWeekSummaries"
                    :key="index">
                    <el-select v-model="item.startWeek" size="mini" style="width:90px">
                        <el-option v-for="(period, i) in editingPeriods" :key="i" :label="weekLabel(i)"
                            :value="i + 1"></el-option>
                    </el-select>
                    <span class="range-sep">至</span>
                    <el-select v-model="item.endWeek" size="mini" style="width:90px">
                        <el-option v-for="(period, i) in editingPeriods" :key="i" :label="weekLabel(i)"
                            :value="i + 1" :disabled="i + 1 < item.startWeek"></el-option>
                    </el-select>
                    <el-date-picker v-model="item.date" type="date" value-format="yyyy-MM-dd" size="mini"
                        placeholder="总结日期（选填）" :picker-options="projectPickerOptions"
                        style="width:170px;margin-left:8px"></el-date-picker>
                    <el-button type="text" class="btn-delete-summary" @click="answerForm.multiWeekSummaries.splice(index, 1)">删除</el-button>
                </div>
            </div>
            <span slot="footer">
                <el-button size="small" @click="answerDialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="savingAnswer" @click="saveAnswerConfig">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    // 将日期数组中连续的日期合并为范围显示，格式 yyyy.MM.dd-yyyy.MM.dd，单独的单独显示
    const formatDateRanges = (dates) => {
        if (!dates.length) return '';
        let sorted = dates.slice().sort();
        let ranges = [];
        let rangeStart = sorted[0];
        let prev = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
            let prevDate = new Date(prev.replace(/-/g, '/'));
            let currDate = new Date(sorted[i].replace(/-/g, '/'));
            if (currDate - prevDate === 86400000) {
                prev = sorted[i];
            } else {
                ranges.push([rangeStart, prev]);
                rangeStart = sorted[i];
                prev = sorted[i];
            }
        }
        ranges.push([rangeStart, prev]);
        let toDot = (d) => d.replace(/-/g, '.');
        return ranges.map(([s, e]) => s === e ? toDot(s) : `${toDot(s)}-${toDot(e)}`).join('、');
    };

    const datesInRange = (start, end) => {
        let dates = [];
        let current = new Date(start.replace(/-/g, '/'));
        let last = new Date(end.replace(/-/g, '/'));
        while (current <= last) {
            let m = String(current.getMonth() + 1).padStart(2, '0');
            let d = String(current.getDate()).padStart(2, '0');
            dates.push(`${current.getFullYear()}-${m}-${d}`);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const dateToStr = (date) => {
        let m = String(date.getMonth() + 1).padStart(2, '0');
        let d = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${m}-${d}`;
    };

    const toDateStr = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val.slice(0, 10);
        let d = new Date(val);
        if (Number.isNaN(d.getTime())) return '';
        return dateToStr(d);
    };

    const emptyQuestions = () => ({
        observeLabel: '观',
        observeText: '',
        reflectLabel: '思',
        reflectText: '',
        questionLabel: '问',
        questionText: '',
    });

    const unwrapApi = (res) => {
        if (!res) return res;
        if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
            return res.data;
        }
        return res;
    };

    const isApiSuccess = (res) => {
        let body = unwrapApi(res);
        return body && (body.code === 0 || body.code === 200);
    };

    const apiMsg = (res) => {
        let body = unwrapApi(res);
        return (body && body.msg) || '';
    };

    export default {
        data() {
            return {
                loading: false,
                savingQuestions: false,
                savingAnswer: false,
                projectStart: '',
                projectEnd: '',
                questionTypes: [
                    { type: 'observe', name: '观察', icon: 'el-icon-view' },
                    { type: 'reflect', name: '思考', icon: 'el-icon-chat-round' },
                    { type: 'question', name: '提问', icon: 'el-icon-question' },
                ],
                phaseList: [],
                // 编辑问题弹窗
                questionDialogVisible: false,
                editingPhase: null,
                editingGroup: 'all',
                syncAllGroups: false,
                questionForm: emptyQuestions(),
                // 编辑作答日期弹窗
                answerDialogVisible: false,
                answerForm: {
                    mode: 'fixedDates',
                    fixedDates: [],
                    weekMinDays: [],
                    summaryMode: 'weekly',
                    weekSummaryDates: [],
                    multiWeekSummaries: [],
                },
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            editingPeriods() {
                return this.editingPhase ? this.editingPhase.periods : [];
            },
            weekPeriodsText() {
                if (!this.editingPeriods.length) return '请先在「编辑阶段」中配置阶段周次';
                let total = this.editingPeriods.reduce((sum, period) => sum + this.weekDayCount(period), 0);
                return `共${this.editingPeriods.length}周：` + this.editingPeriods
                    .map((period, i) => `${this.weekLabel(i)} ${period.start} ~ ${period.end}`)
                    .join('；') + `（共${total}天）`;
            },
            answerPickerOptions() {
                let allowed = {};
                this.editingPeriods.forEach(period => {
                    datesInRange(period.start, period.end).forEach(date => (allowed[date] = true));
                });
                return {
                    disabledDate(date) {
                        return !allowed[dateToStr(date)];
                    },
                };
            },
            projectPickerOptions() {
                let start = this.projectStart;
                let end = this.projectEnd;
                return {
                    disabledDate(date) {
                        if (!start || !end) return false;
                        let str = dateToStr(date);
                        return str < start || str > end;
                    },
                };
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadAll();
                    else this.phaseList = [];
                },
            },
        },
        methods: {
            weekLabel(index) {
                const cn = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
                return `第${cn[index] || index + 1}周`;
            },
            weekDayCount(period) {
                return datesInRange(period.start, period.end).length;
            },
            weekPickerOptions(period) {
                return {
                    disabledDate(date) {
                        let str = dateToStr(date);
                        return str < period.start || str > period.end;
                    },
                };
            },
            isCustomGroup(phase, groupKey) {
                return (phase.customGroups || []).map(String).indexOf(String(groupKey)) !== -1;
            },
            normalizeQuestions(q) {
                let src = q || {};
                return {
                    observeLabel: src.observeLabel || '观',
                    observeText: src.observeText || '',
                    reflectLabel: src.reflectLabel || '思',
                    reflectText: src.reflectText || '',
                    questionLabel: src.questionLabel || '问',
                    questionText: src.questionText || '',
                };
            },
            mapPeriodsFromStage(stageVo) {
                return (stageVo.periods || []).map(p => ({
                    start: p.startDate,
                    end: p.endDate,
                }));
            },
            mapGswItem(vo, stageMap) {
                let stageId = vo.stageId;
                let stage = stageMap[stageId] || {};
                let periods = Array.isArray(vo.periods) && vo.periods.length
                    ? vo.periods.map(p => ({ start: p.startDate || p.start, end: p.endDate || p.end }))
                    : this.mapPeriodsFromStage(stage);

                let groupOptions = (vo.stageUserGroups || []).map(g => ({
                    key: String(g.id),
                    label: g.name,
                }));

                let questionsByGroup = vo.questionsByGroup || {};
                let groups = { all: this.normalizeQuestions(questionsByGroup.all) };
                groupOptions.forEach(g => {
                    groups[g.key] = this.normalizeQuestions(questionsByGroup[g.key] || questionsByGroup.all);
                });

                let summaryJson = vo.summaryJson || {};
                let weekSummaryDates = Array.isArray(summaryJson.weekSummaryDates)
                    ? summaryJson.weekSummaryDates.map(d => d || '')
                    : [];
                let multiWeekSummaries = Array.isArray(summaryJson.multiWeekSummaries)
                    ? summaryJson.multiWeekSummaries.map(item => ({
                        startWeek: item.startWeek,
                        endWeek: item.endWeek,
                        date: item.date || '',
                    }))
                    : [];

                return {
                    id: stageId,
                    projectId: vo.projectId,
                    displayName: vo.stageName || stage.stageName || '',
                    gswEnabled: Number(vo.frontendEnabled) === 1,
                    activeGroup: 'all',
                    customGroups: (vo.customGroups || []).map(String),
                    groupOptions,
                    periods,
                    groups,
                    answerConfig: {
                        mode: vo.answerMode || 'fixedDates',
                        fixedDates: (vo.answerDates || []).slice(),
                        weekMinDays: (vo.weekMinDays || []).slice(),
                        summaryMode: vo.summaryMode || 'weekly',
                        weekSummaryDates,
                        multiWeekSummaries,
                    },
                };
            },
            async loadAll() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    await Promise.all([this.fetchProjectInfo(), this.fetchList()]);
                } finally {
                    this.loading = false;
                }
            },
            async fetchProjectInfo() {
                let res = await this.$Api.StageV2.getProjectInfo({ projectId: this.projectId });
                if (!isApiSuccess(res)) return;
                let data = res.data || {};
                this.projectStart = toDateStr(data.periodStartTime);
                this.projectEnd = toDateStr(data.periodFinishTime);
            },
            async fetchList() {
                let [gswRes, stageRes] = await Promise.all([
                    this.$Api.StageV2.getGswList({ projectId: this.projectId }),
                    this.$Api.StageV2.getStageList({ projectId: this.projectId }),
                ]);
                if (!isApiSuccess(gswRes)) {
                    this.$message.error(apiMsg(gswRes) || '获取观思问配置失败');
                    this.phaseList = [];
                    return;
                }
                let stageMap = {};
                if (isApiSuccess(stageRes)) {
                    (stageRes.data || []).forEach(s => {
                        stageMap[s.id] = s;
                    });
                }
                this.phaseList = (gswRes.data || []).map(item => this.mapGswItem(item, stageMap));
            },
            questionBlocks(phase) {
                let questions = phase.groups[phase.activeGroup] || phase.groups.all;
                return this.questionTypes.map(item => ({
                    type: item.type,
                    icon: item.icon,
                    label: questions[item.type + 'Label'] || item.name,
                    text: questions[item.type + 'Text'],
                }));
            },
            scheduleLines(phase) {
                let config = phase.answerConfig;
                let lines = [];
                phase.periods.forEach((period, index) => {
                    if (config.mode === 'fixedDates') {
                        let weekDates = datesInRange(period.start, period.end);
                        let selected = config.fixedDates.filter(date => weekDates.indexOf(date) !== -1);
                        lines.push({
                            text: `${this.weekLabel(index)}作答：${selected.length ? formatDateRanges(selected) : '未配置'}`,
                            empty: !selected.length,
                        });
                    } else {
                        lines.push({ text: `${this.weekLabel(index)}作答：最少${config.weekMinDays[index] || 1}天` });
                    }
                });
                if (config.summaryMode === 'multiWeek') {
                    if (!config.multiWeekSummaries.length) {
                        lines.push({ text: '总结：未配置', muted: true, empty: true });
                    } else {
                        config.multiWeekSummaries.forEach(item => {
                            let label = item.startWeek === item.endWeek
                                ? this.weekLabel(item.startWeek - 1)
                                : `${this.weekLabel(item.startWeek - 1)} ~ ${this.weekLabel(item.endWeek - 1)}`;
                            lines.push({ text: `${label}总结：${item.date || '未配置'}`, muted: true, empty: !item.date });
                        });
                    }
                } else {
                    phase.periods.forEach((period, index) => {
                        let date = config.weekSummaryDates[index] || '';
                        lines.push({ text: `${this.weekLabel(index)}总结：${date || '未配置'}`, muted: true, empty: !date });
                    });
                }
                return lines;
            },
            hasQuestionsConfigured(phase) {
                let q = (phase.groups && phase.groups.all) || {};
                return !!(q.observeText && q.reflectText && q.questionText);
            },
            hasAnswerConfigured(phase) {
                if (!phase.periods || !phase.periods.length) return false;
                let config = phase.answerConfig || {};
                if (config.mode === 'minDays') {
                    return Array.isArray(config.weekMinDays) && config.weekMinDays.length === phase.periods.length;
                }
                return Array.isArray(config.fixedDates) && config.fixedDates.length > 0;
            },
            async toggleFrontend(phase) {
                let next = phase.gswEnabled ? 0 : 1;
                if (next === 1) {
                    let missing = [];
                    if (!this.hasQuestionsConfigured(phase)) missing.push('问题');
                    if (!this.hasAnswerConfigured(phase)) missing.push('作答日期');
                    if (missing.length) {
                        this.$message.warning(`请先编辑${missing.join('与')}后再启用`);
                        return;
                    }
                }
                this.$set(phase, '_toggling', true);
                try {
                    let res = await this.$Api.StageV2.setGswFrontendEnabled({
                        stageId: phase.id,
                        enabled: next,
                    });
                    if (isApiSuccess(res)) {
                        phase.gswEnabled = next === 1;
                        this.$message.success(next === 1 ? '前端已启用' : '前端已取消启用');
                    } else {
                        this.$message.error(apiMsg(res) || '操作失败');
                    }
                } catch (e) {
                    this.$message.error('操作失败');
                } finally {
                    this.$set(phase, '_toggling', false);
                }
            },
            // ============ 编辑问题 ============
            async openQuestionDialog(phase) {
                this.editingPhase = phase;
                this.editingGroup = 'all';
                this.syncAllGroups = false;
                try {
                    let res = await this.$Api.StageV2.getGswDetail({ stageId: phase.id });
                    if (isApiSuccess(res)) {
                        let mapped = this.mapGswItem(res.data || {}, { [phase.id]: { periods: [], stageName: phase.displayName } });
                        mapped.periods = phase.periods;
                        mapped.groupOptions = mapped.groupOptions.length ? mapped.groupOptions : phase.groupOptions;
                        this.editingPhase = mapped;
                        // 同步列表卡片数据
                        let idx = this.phaseList.findIndex(p => p.id === phase.id);
                        if (idx !== -1) {
                            this.$set(this.phaseList, idx, {
                                ...mapped,
                                activeGroup: this.phaseList[idx].activeGroup || 'all',
                            });
                            this.editingPhase = this.phaseList[idx];
                        }
                    }
                } catch (e) { /* 用列表缓存即可 */ }
                this.fillQuestionForm();
                this.questionDialogVisible = true;
            },
            fillQuestionForm() {
                let questions = (this.editingPhase.groups[this.editingGroup]
                    || this.editingPhase.groups.all
                    || emptyQuestions());
                this.questionForm = { ...questions };
            },
            switchEditingGroup(groupKey) {
                if (this.editingGroup === groupKey) return;
                this.editingGroup = groupKey;
                this.fillQuestionForm();
            },
            async saveQuestions() {
                let form = this.questionForm;
                if (!form.observeText || !form.reflectText || !form.questionText) {
                    this.$message.warning(this.editingGroup === 'all'
                        ? '保存「全部」时请完整填写观察、思考、提问三类问题'
                        : '请完整填写观察、思考、提问三类问题');
                    return;
                }
                if (!this.projectId || !this.editingPhase) return;
                let questionsPayload = {};
                questionsPayload[this.editingGroup] = {
                    observeLabel: form.observeLabel,
                    observeText: form.observeText,
                    reflectLabel: form.reflectLabel,
                    reflectText: form.reflectText,
                    questionLabel: form.questionLabel,
                    questionText: form.questionText,
                };
                this.savingQuestions = true;
                try {
                    let res = await this.$Api.StageV2.saveGsw({
                        projectId: this.projectId,
                        stageId: this.editingPhase.id,
                        saveMode: 'questions',
                        editingGroup: this.editingGroup,
                        syncToAll: this.editingGroup === 'all' ? this.syncAllGroups : false,
                        frontendEnabled: this.editingPhase.gswEnabled ? 1 : 0,
                        questionsByGroup: questionsPayload,
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.questionDialogVisible = false;
                        await this.fetchList();
                    } else {
                        this.$message.error(apiMsg(res) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.savingQuestions = false;
                }
            },
            // ============ 编辑作答日期 ============
            async openAnswerDialog(phase) {
                this.editingPhase = phase;
                try {
                    let res = await this.$Api.StageV2.getGswDetail({ stageId: phase.id });
                    if (isApiSuccess(res)) {
                        let mapped = this.mapGswItem(res.data || {}, {});
                        mapped.periods = mapped.periods.length ? mapped.periods : phase.periods;
                        mapped.groupOptions = mapped.groupOptions.length ? mapped.groupOptions : phase.groupOptions;
                        let idx = this.phaseList.findIndex(p => p.id === phase.id);
                        if (idx !== -1) {
                            this.$set(this.phaseList, idx, {
                                ...mapped,
                                activeGroup: this.phaseList[idx].activeGroup || 'all',
                            });
                            this.editingPhase = this.phaseList[idx];
                        } else {
                            this.editingPhase = mapped;
                        }
                    }
                } catch (e) { /* 用列表缓存即可 */ }

                let config = this.editingPhase.answerConfig;
                let periods = this.editingPhase.periods;
                this.answerForm = {
                    mode: config.mode || 'fixedDates',
                    fixedDates: (config.fixedDates || []).slice(),
                    weekMinDays: periods.map((period, index) => ({
                        value: Math.min(config.weekMinDays[index] || 1, this.weekDayCount(period)),
                    })),
                    summaryMode: config.summaryMode || 'weekly',
                    weekSummaryDates: periods.map((period, index) => ({
                        date: config.weekSummaryDates[index] || '',
                    })),
                    multiWeekSummaries: (config.multiWeekSummaries || []).map(item => ({ ...item })),
                };
                this.answerDialogVisible = true;
            },
            generateAnswerDates() {
                let dateMap = {};
                this.editingPeriods.forEach(period => {
                    datesInRange(period.start, period.end).forEach(date => (dateMap[date] = true));
                });
                this.answerForm.fixedDates = Object.keys(dateMap).sort();
            },
            addMultiSummary() {
                this.answerForm.multiWeekSummaries.push({
                    startWeek: 1,
                    endWeek: Math.min(2, this.editingPeriods.length),
                    date: '',
                });
            },
            async saveAnswerConfig() {
                if (!this.editingPeriods.length) {
                    this.$message.warning('请先在「阶段管理」中配置阶段周次');
                    return;
                }
                let form = this.answerForm;
                if (form.mode === 'fixedDates' && !form.fixedDates.length) {
                    this.$message.warning('请至少选择一个固定作答日期（须在阶段周次范围内）');
                    return;
                }
                for (let i = 0; i < form.multiWeekSummaries.length; i++) {
                    let item = form.multiWeekSummaries[i];
                    if (item.endWeek < item.startWeek) item.endWeek = item.startWeek;
                }
                let summaryJson = form.summaryMode === 'weekly'
                    ? {
                        weekSummaryDates: form.weekSummaryDates.map(item => item.date || null),
                    }
                    : {
                        multiWeekSummaries: form.multiWeekSummaries.map(item => ({
                            startWeek: item.startWeek,
                            endWeek: item.endWeek,
                            date: item.date || null,
                        })),
                    };
                this.savingAnswer = true;
                try {
                    let res = await this.$Api.StageV2.saveGsw({
                        projectId: this.projectId,
                        stageId: this.editingPhase.id,
                        saveMode: 'answer',
                        answerMode: form.mode,
                        answerDates: form.mode === 'fixedDates' ? form.fixedDates.slice().sort() : [],
                        weekMinDays: form.mode === 'minDays' ? form.weekMinDays.map(item => item.value) : [],
                        summaryMode: form.summaryMode,
                        summaryJson,
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.answerDialogVisible = false;
                        await this.fetchList();
                    } else {
                        this.$message.error(apiMsg(res) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.savingAnswer = false;
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    .observe-config {
        padding: 20px 25px;
        box-sizing: border-box;

        .phase-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .phase-card {
            background: #fff;
            border: 1px solid #93C5FD;
            border-radius: 10px;
            padding: 16px 18px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

            &.is-disabled {
                opacity: 0.72;
                border-color: #D1D5DB;
                background: #FAFAFA;
            }

            .head-left {
                .pin-icon {
                    color: #EC4899;
                    margin-right: 8px;
                }

                .phase-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #303133;
                    margin-right: 8px;
                }
            }
        }

        .gsw-schedule {
            background: #F8FAFC;
            border-radius: 6px;
            padding: 10px 14px;
            margin-top: 12px;

            .schedule-title {
                font-size: 13px;
                font-weight: 600;
                color: #303133;
                margin-bottom: 6px;
            }

            .schedule-line {
                font-size: 13px;
                color: #606266;
                line-height: 1.9;

                &.is-muted {
                    color: #909399;
                }

                &.is-empty {
                    color: #C0C4CC;
                }
            }
        }

        .phase-blocks {
            display: flex;
            gap: 12px;
            margin-top: 12px;

            .phase-block {
                flex: 1;
                border: 1px solid #EBEEF5;
                border-radius: 6px;
                padding: 10px 12px;

                .block-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #409EFF;
                    margin-bottom: 6px;

                    i {
                        margin-right: 4px;
                    }
                }

                .block-text {
                    font-size: 13px;
                    color: #606266;
                    line-height: 1.6;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 3;
                    overflow: hidden;
                    word-break: break-word;
                }
            }
        }
    }

    .group-tabs {
        margin-top: 12px;

        .group-tab {
            display: inline-flex;
            align-items: center;
            padding: 5px 12px;
            margin-right: 8px;
            border: 1px solid #DCDFE6;
            border-radius: 4px;
            font-size: 13px;
            color: #606266;
            cursor: pointer;
            user-select: none;

            &:hover {
                color: #409EFF;
                border-color: #409EFF;
            }

            &.is-active {
                color: #409EFF;
                border-color: #409EFF;
                background: #ECF5FF;
            }

            .group-tag {
                font-style: normal;
                font-size: 10px;
                padding: 0 4px;
                margin-left: 5px;
                border-radius: 3px;
                line-height: 16px;

                &.is-shared {
                    color: #909399;
                    background: #F4F4F5;
                }

                &.is-custom {
                    color: #E6A23C;
                    background: #FDF6EC;
                }
            }
        }
    }

    .dialog-subtitle {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 10px;
    }

    .modal-group-tabs {
        margin: 0 0 8px;
    }

    .form-hint {
        font-size: 12px;
        color: #909399;
        line-height: 1.6;
        margin: 6px 0;
    }

    .sync-checkbox {
        margin-bottom: 12px;
    }

    .section-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin: 14px 0 8px;

        &:first-of-type {
            margin-top: 0;
        }
    }

    .week-periods {
        background: #F8FAFC;
        border-radius: 6px;
        padding: 8px 12px;
        margin: 10px 0;
        font-size: 13px;

        .week-periods-label {
            color: #909399;
            margin-right: 8px;
        }

        .week-periods-text {
            color: #606266;
        }
    }

    .answer-panel {
        margin-bottom: 6px;
    }

    .min-week-row {
        margin-bottom: 8px;
        font-size: 13px;
        color: #606266;

        .min-week-label {
            width: 48px;
            flex-shrink: 0;
        }

        .min-week-range {
            color: #909399;
            margin-right: 12px;
        }

        .range-sep {
            margin: 0 6px;
            color: #909399;
        }

        .btn-delete-summary {
            margin-left: 10px;
            color: #F56C6C;
        }
    }
</style>
